import { Controller } from '@hotwired/stimulus'
import type { App } from 'vue'

import { createApp } from '@app/adapters/create-app'
import { SfLog } from '@app/adapters/logger'

import CommentEditor from '@app/application/check_lists/comment-editor.vue'

const TEMPLATE_SELECTOR = "[data-behaviour='check-link-comment-form-template']"
const EDIT_TEMPLATE_SELECTOR =
  "[data-behaviour='check-link-comment-edit-template']"
const FORM_SELECTOR = "[data-behaviour~='form']"
const COMMENTABLE_ID_SELECTOR = "input[name='commentable_id']"

export default class extends Controller<HTMLElement> {
  static targets = [
    'replyArea',
    'display',
    'editorMount',
    'hiddenInput',
    'cancelButton',
    'submitButton',
    'error',
  ]

  static values = {
    initialText: String,
    placeholder: String,
    commentableId: String,
  }

  declare initialTextValue: string
  declare placeholderValue: string
  declare commentableIdValue: string

  declare replyAreaTarget: HTMLElement
  declare hasReplyAreaTarget: boolean
  declare displayTarget: HTMLElement
  declare hasDisplayTarget: boolean
  declare hasEditorMountTarget: boolean
  declare hiddenInputTarget: HTMLInputElement | HTMLTextAreaElement
  declare hasHiddenInputTarget: boolean
  declare cancelButtonTarget: HTMLButtonElement
  declare hasCancelButtonTarget: boolean
  declare submitButtonTarget: HTMLButtonElement
  declare hasSubmitButtonTarget: boolean
  declare errorTarget: HTMLElement
  declare hasErrorTarget: boolean

  private vueApp: App | null = null
  private formHost: HTMLElement | null = null
  private formElement: HTMLFormElement | null = null
  private submitting = false
  private boundOnAjaxSend = (): void => this.onAjaxSend()
  private boundOnAjaxComplete = (): void => this.onAjaxComplete()
  private boundClose = (): void => this.close()
  private boundShowError = (event: Event): void => this.showError(event)

  connect(): void {
    this.element.addEventListener('checklist-comment:created', this.boundClose)
    this.element.addEventListener(
      'checklist-comment:error',
      this.boundShowError
    )
  }

  disconnect(): void {
    this.element.removeEventListener(
      'checklist-comment:created',
      this.boundClose
    )
    this.element.removeEventListener(
      'checklist-comment:error',
      this.boundShowError
    )
    this.teardownForm()
  }

  open(event?: Event): void {
    event?.preventDefault()
    const template =
      document.querySelector<HTMLTemplateElement>(TEMPLATE_SELECTOR)
    if (!template) {
      SfLog.warn(
        'check-list--comment-form: form template not found; cannot open reply form'
      )
      return
    }
    this.openForm(
      template,
      this.hasReplyAreaTarget ? this.replyAreaTarget : null
    )
  }

  edit(event?: Event): void {
    event?.preventDefault()
    const template = this.element.querySelector<HTMLTemplateElement>(
      EDIT_TEMPLATE_SELECTOR
    )
    if (!template) {
      SfLog.warn(
        'check-list--comment-form: edit template not found; cannot open edit form'
      )
      return
    }
    this.openForm(template, this.hasDisplayTarget ? this.displayTarget : null)
  }

  private openForm(
    template: HTMLTemplateElement,
    hideTarget: HTMLElement | null
  ): void {
    if (this.formHost !== null) return

    const host = document.createElement('div')
    host.appendChild(template.content.cloneNode(true))

    const commentableIdInput = host.querySelector<HTMLInputElement>(
      COMMENTABLE_ID_SELECTOR
    )
    if (commentableIdInput && this.commentableIdValue) {
      commentableIdInput.value = this.commentableIdValue
    }

    if (hideTarget) hideTarget.hidden = true
    this.formHost = host
    this.element.appendChild(host)
    document.dispatchEvent(new CustomEvent('multiFileUploadBehaviour'))
  }

  cancel(event: Event): void {
    event.preventDefault()
    if (this.submitting) return
    this.close()
  }

  submit(event: Event): void {
    event.preventDefault()
    if (!this.formElement || this.submitting) return
    this.clearError()
    this.submitting = true
    this.formElement.requestSubmit()
  }

  close(): void {
    this.teardownForm()
    if (this.hasReplyAreaTarget) this.replyAreaTarget.hidden = false
    if (this.hasDisplayTarget) this.displayTarget.hidden = false
  }

  editorMountTargetConnected(element: HTMLElement): void {
    if (this.vueApp) return
    if (!this.hasHiddenInputTarget) {
      SfLog.warn(
        'check-list--comment-form: hiddenInput target missing; editor not mounted'
      )
      this.close()
      return
    }

    try {
      this.vueApp = createApp(CommentEditor, {
        initialText: this.initialTextValue,
        placeholder: this.placeholderValue,
        updateText: (value: string) => {
          this.hiddenInputTarget.value = value
        },
      })
      this.vueApp.mount(element)
    } catch (error) {
      SfLog.error('check-list--comment-form: failed to mount comment editor', {
        error,
      })
      this.close()
      this.submitting = false
      this.setButtonsDisabled(false)
      return
    }

    this.formElement =
      this.element.querySelector<HTMLFormElement>(FORM_SELECTOR)
    this.formElement?.addEventListener('ajax:send', this.boundOnAjaxSend)
    this.formElement?.addEventListener(
      'ajax:complete',
      this.boundOnAjaxComplete
    )
  }

  editorMountTargetDisconnected(): void {
    this.unmountEditor()
  }

  private teardownForm(): void {
    this.unmountEditor()
    this.teardownFileUploads()
    this.formHost?.remove()
    this.formHost = null
    this.submitting = false
  }

  private teardownFileUploads(): void {
    this.formHost
      ?.querySelectorAll("[data-behaviour~='multi-file-upload-wrapper']")
      .forEach((wrapper) =>
        wrapper.dispatchEvent(new CustomEvent('multiFileUploadTeardown'))
      )
  }

  private showError(event: Event): void {
    // The form stays open on a validation error, so clear the in-flight guard
    // here — the error response does not reliably fire ajax:complete, and without
    // this the user can never resubmit after fixing the error.
    this.submitting = false
    this.setButtonsDisabled(false)

    if (!this.hasErrorTarget) return
    const { messages } =
      (event as CustomEvent<{ messages?: string[] }>).detail ?? {}
    const text = (messages ?? []).join('. ')
    this.errorTarget.textContent = text
    this.errorTarget.hidden = text.length === 0
  }

  private clearError(): void {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ''
    this.errorTarget.hidden = true
  }

  private unmountEditor(): void {
    this.formElement?.removeEventListener('ajax:send', this.boundOnAjaxSend)
    this.formElement?.removeEventListener(
      'ajax:complete',
      this.boundOnAjaxComplete
    )
    this.formElement = null
    this.vueApp?.unmount()
    this.vueApp = null
  }

  private onAjaxSend(): void {
    this.setButtonsDisabled(true)
  }

  private onAjaxComplete(): void {
    this.submitting = false
    this.setButtonsDisabled(false)
  }

  private setButtonsDisabled(disabled: boolean): void {
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.disabled = disabled
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = disabled
    }
  }
}
